import type { GraphqlError } from './error-handling/error-component';

import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { relayStylePagination } from '@apollo/client/utilities';
import { type ReactNode, useMemo, useState } from 'react';

import { ErrorComponent } from './error-handling/error-component';
import { problemsHandler } from './error-handling/problems-handler';
import { keycloak } from './service/authentication/keycloak-provider';
import { devLog } from './utils';
import { mergeById, mergeByUniqueProp } from './utils/apollo-helpers';
import { getSessionStorageItem, removeSessionStorageItem } from './utils/session-storage';
import { viewerFetch } from './utils/viewer-fetch';

const MIN_VALIDITY_SECONDS = 300;

if (process.env.REACT_APP_MODE === 'development') {
  loadErrorMessages();
  loadDevMessages();
}

const adminHeaders = setContext(async (_, { headers }) => {
  // Refresh token if expired
  await keycloak.updateToken(MIN_VALIDITY_SECONDS).catch(keycloak.login);

  return {
    headers: {
      ...headers,
      authorization: `Bearer ${keycloak?.token}`,
      'content-type': 'application/json',
    },
  };
});

export function AdminApiProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<GraphqlError>();

  const adminClient = useMemo(
    () =>
      new ApolloClient({
        link: from([
          adminHeaders,
          problemsHandler(setError),
          new HttpLink({ uri: process.env.REACT_APP_ADMIN_APOLLO_CLIENT_URI }),
        ]),
        cache: new InMemoryCache({
          typePolicies: {
            Webcast: {
              merge: true,
              fields: {
                contents: { merge: mergeByUniqueProp('language') },
                recordingSettings: { merge: true },
                subtitleSettings: { merge: true },
              },
            },
            CurrentUser: { merge: true },
            LiveStreamingSettings: { merge: true },
            StandardLanguage: { merge: true },
            // `keyFields` specifies `lsproId` as the unique identifier for caching
            LiveStreamProSettings: {
              keyFields: ['lsproId'],
              merge: true,
              fields: {
                themes: { merge: mergeById },
                supportedFonts: { merge: mergeById },
              },
            },
            Query: {
              fields: {
                currentUser: { merge: true },
                configuration: { merge: true },
                lsproSettings: { merge: true },
                webcast: { merge: true },
                webcasts: relayStylePagination(),
              },
            },
          },
        }),
      }),
    []
  );

  return (
    <ApolloProvider client={adminClient}>
      <ErrorComponent error={error} onClose={() => setError(undefined)}>
        {children}
      </ErrorComponent>
    </ApolloProvider>
  );
}

const viewerHeaders = setContext((_, { headers }) => {
  const token = getSessionStorageItem('viewerAccessToken');

  const data = {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(window.document.referrer ? { 'X-Referrer': new URL(window.document.referrer).hostname } : {}),
      'content-type': 'application/json',
    },
  };

  devLog('👥 Current API headers:', data.headers, { memoize: true });

  return data;
});

// Clear any existing SSO tokens. We'll get a new one if needed during auth flow.
removeSessionStorageItem('viewerAccessToken');
// Clear Old-Last-Modified entries on mount to avoid stale data across sessions
Object.keys(sessionStorage).forEach((key) => {
  if (key === 'Old-Last-Modified' || key.startsWith('Old-Last-Modified:')) {
    removeSessionStorageItem(key);
  }
});

export function ViewerApiProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<GraphqlError>();

  const viewerClient = useMemo(
    () =>
      new ApolloClient({
        cache: new InMemoryCache(),
        link: from([
          viewerHeaders,
          problemsHandler(setError),
          new HttpLink({ uri: process.env.REACT_APP_VIEWER_APOLLO_CLIENT_URI, fetch: viewerFetch }),
        ]),
      }),
    []
  );

  return (
    <ApolloProvider client={viewerClient}>
      <ErrorComponent error={error} onClose={() => setError(undefined)}>
        {children}
      </ErrorComponent>
    </ApolloProvider>
  );
}
